import React, { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check, Loader2, Pencil, PlayCircle, RotateCcw, Send, Trash2, X, XCircle } from "lucide-react";
import type { SampleRecord, SampleStatus } from "@/types";
import { SAMPLE_STATUS_LABELS } from "./SampleStatusBadge";

/** The next steps a sample can take from each status */
const NEXT: Record<SampleStatus, SampleStatus[]> = {
  REQUESTED: ["IN_PROGRESS", "DISPATCHED", "CANCELLED"],
  IN_PROGRESS: ["DISPATCHED", "CANCELLED"],
  DISPATCHED: ["APPROVED", "REVISION", "REJECTED"],
  REVISION: ["IN_PROGRESS", "DISPATCHED", "CANCELLED"],
  APPROVED: [],
  REJECTED: [],
  CANCELLED: [],
};

const EDITABLE: SampleStatus[] = ["REQUESTED", "IN_PROGRESS", "REVISION"];

const BUTTON: Record<SampleStatus, { label: string; icon: typeof Check; variant: "default" | "outline" | "destructive" }> = {
  REQUESTED: { label: "Requested", icon: PlayCircle, variant: "outline" },
  IN_PROGRESS: { label: "Start work", icon: PlayCircle, variant: "outline" },
  DISPATCHED: { label: "Dispatch", icon: Send, variant: "default" },
  APPROVED: { label: "Buyer approved", icon: Check, variant: "default" },
  REVISION: { label: "Revision asked", icon: RotateCcw, variant: "outline" },
  REJECTED: { label: "Rejected", icon: XCircle, variant: "outline" },
  CANCELLED: { label: "Cancel", icon: X, variant: "outline" },
};

const COMMENT_REQUIRED: SampleStatus[] = ["REJECTED", "REVISION"];

interface Props {
  sample: SampleRecord;
  onChanged: () => void;
  onEdit: (s: SampleRecord) => void;
  /** Smaller buttons for table rows */
  compact?: boolean;
}

/** Buttons for the sample's next steps, with the dialog each one needs (courier and AWB, the buyer's comments) */
export function SampleActions({ sample, onChanged, onEdit, compact }: Props) {
  const { hasPermission } = useAuth();
  const { success, error } = useToast();
  const canUpdate = hasPermission("sample.update");
  const canDelete = hasPermission("sample.delete");
  const [target, setTarget] = useState<SampleStatus | null>(null);
  const [courier, setCourier] = useState("");
  const [awb, setAwb] = useState("");
  const [dispatchedOn, setDispatchedOn] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const steps = NEXT[sample.sample_status] || [];
  const size = compact ? "h-7 text-[11px] px-2" : "h-8 text-xs";

  const open = (st: SampleStatus) => {
    // Starting work needs no details, so it happens at once
    if (st === "IN_PROGRESS") return send(st);
    setCourier(sample.courier || "");
    setAwb(sample.awb_number || "");
    setDispatchedOn(new Date().toISOString().slice(0, 10));
    setComment("");
    setTarget(st);
  };

  const send = async (st: SampleStatus, extra: Record<string, any> = {}) => {
    setBusy(true);
    try {
      const res: any = await api.patch(`/api/Sample/ChangeStatus/${sample.id}`, { status: st, ...extra });
      success(`Sample ${SAMPLE_STATUS_LABELS[st].toLowerCase()}`, res?.advanced_to ? `The deal moved to ${res.advanced_to}` : "");
      setTarget(null);
      onChanged();
    } catch (err: any) {
      error("Could not update the sample", err.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };

  const submit = () => {
    if (!target) return;
    if (target === "DISPATCHED") {
      send(target, { courier: courier.trim(), awb_number: awb.trim() || undefined, dispatched_on: dispatchedOn || undefined, comment: comment.trim() || undefined });
    } else {
      send(target, { comment: comment.trim() || undefined });
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/api/Sample/Delete/${sample.id}`);
      success("Sample deleted", "");
      setConfirmDelete(false);
      onChanged();
    } catch (err: any) {
      error("Could not delete", err.message || "");
    } finally {
      setBusy(false);
    }
  };

  if (!canUpdate && !canDelete) return null;

  return (
    <>
      <div className="flex flex-wrap items-center gap-1.5">
        {canUpdate &&
          steps.map((st) => {
            const b = BUTTON[st];
            const Icon = b.icon;
            return (
              <Button key={st} size="sm" variant={b.variant} className={`${size} gap-1`} disabled={busy} onClick={() => open(st)}>
                <Icon className="size-3.5" /> {b.label}
              </Button>
            );
          })}
        {canUpdate && EDITABLE.includes(sample.sample_status) && (
          <Button size="sm" variant="ghost" className={`${size} gap-1`} onClick={() => onEdit(sample)} title="Edit details">
            <Pencil className="size-3.5" />
          </Button>
        )}
        {canDelete && sample.sample_status === "REQUESTED" && (
          <Button size="sm" variant="ghost" className={`${size} text-red-600`} onClick={() => setConfirmDelete(true)} title="Delete">
            <Trash2 className="size-3.5" />
          </Button>
        )}
      </div>

      <Dialog open={!!target} onOpenChange={(o) => !o && !busy && setTarget(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {target === "DISPATCHED" ? "Dispatch sample" : target === "APPROVED" ? "Buyer approved the sample" : target === "REVISION" ? "Buyer wants a revision" : target === "REJECTED" ? "Buyer rejected the sample" : "Cancel sample"}
            </DialogTitle>
            <DialogDescription>
              {sample.sample_number} · {sample.product_name}
              {target === "APPROVED" ? ". The deal moves on from Sampling." : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {target === "DISPATCHED" && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Courier *</Label>
                    <Input value={courier} onChange={(e) => setCourier(e.target.value)} placeholder="DTDC, Blue Dart, Hand delivery..." />
                  </div>
                  <div className="space-y-1.5">
                    <Label>AWB / tracking no.</Label>
                    <Input value={awb} onChange={(e) => setAwb(e.target.value)} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Dispatched on</Label>
                  <Input type="date" value={dispatchedOn} onChange={(e) => setDispatchedOn(e.target.value)} />
                </div>
              </>
            )}
            <div className="space-y-1.5">
              <Label>
                {target && COMMENT_REQUIRED.includes(target) ? "Buyer's comments *" : target === "APPROVED" ? "Buyer's comments (optional)" : "Note (optional)"}
              </Label>
              <Textarea
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={target === "REVISION" ? "What should change? e.g. shade slightly greener than the reference" : target === "REJECTED" ? "Why was it rejected?" : ""}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)} disabled={busy}>
              Back
            </Button>
            <Button
              variant={target === "REJECTED" || target === "CANCELLED" ? "destructive" : "default"}
              onClick={submit}
              disabled={busy || (target === "DISPATCHED" && !courier.trim()) || (!!target && COMMENT_REQUIRED.includes(target) && !comment.trim())}
            >
              {busy ? <Loader2 className="size-4 animate-spin mr-1.5" /> : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmDelete} onOpenChange={(o) => !o && !busy && setConfirmDelete(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete sample</DialogTitle>
            <DialogDescription>
              Remove <strong className="text-foreground">{sample.sample_number}</strong>? It was never worked on, so nothing else is affected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(false)} disabled={busy}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={remove} disabled={busy}>
              {busy ? <Loader2 className="size-4 animate-spin mr-1.5" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
